/**
 * FormatMarkdown — Renders markdown-style AI text as clean formatted JSX.
 * Handles: ## headers, ### sub-headers, - bullet points, **bold**, *italic*, numbered lists
 */
export function FormatMarkdown({ text, className = "" }) {
  if (!text) return null;

  const lines = text.split("\n").filter(l => l.trim());

  return (
    <div className={`space-y-2 ${className}`}>
      {lines.map((line, i) => {
        const trimmed = line.trim();

        // Headers
        if (trimmed.startsWith("### "))
          return <h4 key={i} className="text-xs font-bold text-white uppercase mt-3 mb-1">{trimmed.replace(/^###\s*/, "").replace(/\*\*/g, "")}</h4>;
        if (trimmed.startsWith("## "))
          return <h3 key={i} className="text-sm font-bold text-[#ccff00] uppercase mt-4 mb-1">{trimmed.replace(/^##\s*/, "").replace(/\*\*/g, "")}</h3>;

        // Numbered list items (1. 2. 3. etc)
        const numMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
        if (numMatch) {
          return (
            <div key={i} className="flex items-start gap-2 text-sm text-[#a3a3a3] leading-relaxed">
              <span className="text-[#ccff00] font-mono font-bold shrink-0 w-5 text-right">{numMatch[1]}.</span>
              <span>{renderInline(numMatch[2])}</span>
            </div>
          );
        }

        // Bullet points
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          const content = trimmed.replace(/^[-*]\s*/, "");
          return (
            <div key={i} className="flex items-start gap-2 text-sm text-[#a3a3a3] leading-relaxed">
              <span className="text-[#ccff00] mt-1 shrink-0">•</span>
              <span>{renderInline(content)}</span>
            </div>
          );
        }

        // Empty line / separator
        if (!trimmed) return <div key={i} className="h-2" />;

        // Regular paragraph
        return <p key={i} className="text-sm text-[#a3a3a3] leading-relaxed">{renderInline(trimmed)}</p>;
      })}
    </div>
  );
}

function renderInline(text) {
  // Handle **bold** and *italic*
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/s);
    if (boldMatch) {
      if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
      parts.push(<strong key={key++} className="text-white font-semibold">{boldMatch[2]}</strong>);
      remaining = boldMatch[3];
      continue;
    }

    // Italic: *text*
    const italicMatch = remaining.match(/^(.*?)\*(.+?)\*(.*)/s);
    if (italicMatch) {
      if (italicMatch[1]) parts.push(<span key={key++}>{italicMatch[1]}</span>);
      parts.push(<em key={key++} className="text-[#a3a3a3] italic">{italicMatch[2]}</em>);
      remaining = italicMatch[3];
      continue;
    }

    // No more matches
    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }

  return parts;
}
